import React, {useMemo} from "react";
import {
    ArrowLeftRight, Clock, CreditCard, History, Lock, Minus, PackagePlus, Pencil, Percent, Plus, RefreshCw, ShoppingCart,
    StickyNote, Tag, Trash2, User, UserRound, X,
} from "lucide-react";
import ActionMenu from "../Common/ActionMenu";
import {computeLine, taxBreakdown} from "./posMath";
import {Thumb} from "./PosProductGrid";
import {discountLabel, formatMoney} from "./PosModals";

// Panier de la caisse web — dessin de la caisse desktop (electron-pos
// CartPanel.jsx) : barre d'outils compacte, pastille client teintée, lignes à
// plat séparées par un filet, TVA ventilée par taux, gros bouton « Payer ».

const money = (v) => (Number(v) || 0).toLocaleString("fr-FR", {minimumFractionDigits: 2, maximumFractionDigits: 2});

const IconBtn = ({title, onClick, disabled, tone = "text-gray-600 dark:text-gray-300", children}) => (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
            className={`w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 ${tone}`}>
        {children}
    </button>
);

const CartRow = ({line, selected, onSelect, onQty, onSetQty, onDiscount, onPrice, onNote, onRemove}) => {
    const r = computeLine(line);
    const priceEdited = line.basePrice !== undefined && line.basePrice !== line.price;
    return (
        <li className={`border-b border-gray-100 dark:border-gray-700/70 ${selected ? "bg-primary/[0.08]" : ""}`}>
            <button type="button" onClick={() => onSelect(selected ? null : line.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left">
                <Thumb image={line.image} color={line.color} label={line.name}
                       className="w-11 h-11 rounded-[10px] flex-shrink-0" initialsClass="text-[13px]"/>
                <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2 tracking-[-0.01em]">
                        {line.name}
                    </span>
                    <span className="flex flex-wrap items-center gap-1.5 mt-1">
                        <span className="px-2 py-[2px] rounded-full bg-gray-100 dark:bg-gray-700 text-[11px] font-semibold text-gray-800 dark:text-gray-200 tabular-nums">
                            ×{line.quantity}
                        </span>
                        {line.discounts.map((d) => (
                            <span key={d.lineDiscountId}
                                  className="inline-flex items-center gap-1 max-w-[150px] px-2 py-[3px] rounded-full bg-red-500/10 text-red-600 dark:text-red-400 text-[10px] font-semibold">
                                <Tag className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2.4}/>
                                <span className="truncate">{d.name} (−{discountLabel(d)})</span>
                            </span>
                        ))}
                        {line.isFree && (
                            <span className="px-2 py-[3px] rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-300 text-[10px] font-semibold">
                                Vente libre
                            </span>
                        )}
                        {priceEdited && (
                            <span className="px-2 py-[3px] rounded-full bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[10px] font-semibold">
                                Prix modifié
                            </span>
                        )}
                        {line.note && (
                            <span className="inline-flex items-center gap-1 max-w-[140px] px-2 py-[3px] rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px] font-semibold">
                                <StickyNote className="w-2.5 h-2.5 flex-shrink-0"/>
                                <span className="truncate">{line.note}</span>
                            </span>
                        )}
                    </span>
                </span>
                <span className="text-right flex-shrink-0">
                    <span className="block text-sm font-bold text-gray-900 dark:text-white tabular-nums">{money(r.total)}</span>
                    {r.discount > 0
                        ? <span className="block text-[11px] text-gray-400 line-through tabular-nums">{money(r.gross)}</span>
                        : <span className="block text-[11px] text-gray-400 tabular-nums">{money(line.price)} / u</span>}
                </span>
            </button>

            {selected && (
                <div className="flex items-center gap-1.5 px-3 pb-2.5">
                    <div className="flex items-center rounded-full bg-gray-100 dark:bg-gray-700 p-0.5">
                        <button type="button" aria-label="Diminuer" onClick={() => onQty(line, -1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-600">
                            <Minus className="w-4 h-4"/>
                        </button>
                        <input type="number" min="1" value={line.quantity} aria-label="Quantité"
                               onChange={(e) => {
                                   const q = Math.floor(Number(e.target.value));
                                   if (q > 0) onSetQty(line, q);
                               }}
                               className="w-10 bg-transparent text-center text-sm font-semibold tabular-nums text-gray-900 dark:text-white focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"/>
                        <button type="button" aria-label="Augmenter" onClick={() => onQty(line, 1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center bg-primary text-white hover:bg-primary-dark">
                            <Plus className="w-4 h-4"/>
                        </button>
                    </div>
                    <span className="flex-1"/>
                    <IconBtn title="Remise sur la ligne" onClick={() => onDiscount(line)} tone="text-primary"><Percent className="w-4 h-4"/></IconBtn>
                    <IconBtn title="Modifier le prix" onClick={() => onPrice(line)}><Pencil className="w-4 h-4"/></IconBtn>
                    {!line.isFree && <IconBtn title="Note" onClick={() => onNote(line)}><StickyNote className="w-4 h-4"/></IconBtn>}
                    <IconBtn title="Supprimer" onClick={() => onRemove(line.id)} tone="text-red-600"><Trash2 className="w-4 h-4"/></IconBtn>
                </div>
            )}
        </li>
    );
};

const PosCart = ({
                     sale, totals, employees, employeeId, onEmployee, selectedLineId, onSelectLine,
                     onQty, onSetQty, onLineDiscount, onLinePrice, onLineNote, onRemoveLine,
                     onClient, onRemoveClient, onTicketDiscount, onNote, onClearNote, onClear, onPay,
                     onRefresh, refreshing, onCloseJournal,
                     onFreeItem, onHold, heldCount = 0, onShowHeld, onFunds, onActivity,
                 }) => {
    const isEmpty = sale.lines.length === 0;
    const tva = useMemo(() => taxBreakdown(sale.lines), [sale.lines]);

    return (
        <div className="flex flex-col flex-1 h-full min-h-0 bg-white dark:bg-gray-800">
            {/* Barre d'outils */}
            <div className="flex items-center gap-1.5 px-3 pt-2 pb-1.5 flex-shrink-0">
                <label className="flex-1 min-w-0 flex items-center gap-2 h-9 pl-3 pr-1 rounded-full bg-gray-100 dark:bg-gray-700">
                    <UserRound className="w-4 h-4 text-gray-500 flex-shrink-0"/>
                    <select value={employeeId || ""} onChange={(e) => onEmployee(e.target.value)} aria-label="Vendeur"
                            className="flex-1 min-w-0 bg-transparent text-[13px] font-semibold text-gray-800 dark:text-gray-100 focus:outline-none cursor-pointer truncate">
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                </label>
                <button type="button" onClick={onShowHeld} title="Tickets en attente" aria-label={`Tickets en attente (${heldCount})`}
                        className="relative w-9 h-9 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Clock className="w-4 h-4"/>
                    {heldCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center tabular-nums">
                            {heldCount}
                        </span>
                    )}
                </button>
                <IconBtn title="Actualiser le catalogue" onClick={onRefresh} disabled={refreshing}>
                    <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}/>
                </IconBtn>
                <IconBtn title="Clôturer la caisse" onClick={onCloseJournal} tone="text-red-500"><Lock className="w-4 h-4"/></IconBtn>
                <ActionMenu label="Plus d'actions" trigger={{variant: "ghost", size: "sm"}} items={[
                    {label: "Vente libre (F8)", icon: PackagePlus, onClick: onFreeItem},
                    {label: "Remise sur le ticket", icon: Percent, onClick: onTicketDiscount, disabled: isEmpty},
                    {label: sale.note ? "Modifier la note" : "Note de la vente", icon: StickyNote, onClick: onNote},
                    {label: "Mettre en attente", icon: Clock, onClick: onHold, disabled: isEmpty},
                    {divider: true},
                    {label: "Entrée / sortie d'espèces", icon: ArrowLeftRight, onClick: onFunds},
                    {label: "Activité de la session", icon: History, onClick: onActivity},
                    {divider: true},
                    {label: "Vider le panier", icon: Trash2, onClick: onClear, danger: true, disabled: isEmpty},
                ]}/>
            </div>

            {/* Client */}
            <div className="flex gap-1.5 px-3 pb-2 flex-shrink-0">
                <button type="button" onClick={onClient}
                        className="flex-1 min-w-0 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-primary/[0.08] text-primary font-semibold text-sm tracking-[-0.01em] hover:bg-primary/15">
                    <User className="w-4 h-4 flex-shrink-0"/>
                    <span className="truncate">{sale.client ? sale.client.name : "Ajouter un client"}</span>
                    {sale.client?.clientTypeName && (
                        <span className="px-1.5 py-0.5 rounded-md bg-primary text-white text-[10px] font-bold uppercase">{sale.client.clientTypeName}</span>
                    )}
                </button>
                {sale.client && (
                    <button type="button" onClick={onRemoveClient} aria-label="Retirer le client"
                            className="w-8 h-8 self-center rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600">
                        <X className="w-3 h-3" strokeWidth={2.5}/>
                    </button>
                )}
            </div>

            {/* Lignes */}
            <ul className="flex-1 min-h-0 overflow-y-auto overscroll-contain border-t border-gray-100 dark:border-gray-700/70">
                {sale.lines.map((l) => (
                    <CartRow key={l.id} line={l} selected={selectedLineId === l.id} onSelect={onSelectLine}
                             onQty={onQty} onSetQty={onSetQty} onDiscount={onLineDiscount} onPrice={onLinePrice} onNote={onLineNote}
                             onRemove={onRemoveLine}/>
                ))}
                {isEmpty && (
                    <li className="h-full flex flex-col items-center justify-center text-center py-8 text-gray-400">
                        <ShoppingCart className="w-10 h-10 mb-2 text-gray-300 dark:text-gray-600" strokeWidth={1.5}/>
                        <span className="text-sm">Panier vide</span>
                        <span className="text-xs mt-0.5">Touchez un produit ou scannez un code-barres</span>
                    </li>
                )}
            </ul>

            {/* Totaux */}
            <div className="flex-shrink-0 px-4 pt-2.5 pb-2 border-t border-gray-100 dark:border-gray-700/70">
                {totals.discount > 0 && (
                    <>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Sous-total</span><span className="tabular-nums">{money(totals.gross)} DH</span>
                        </div>
                        <div className="flex justify-between text-xs font-bold text-red-600 dark:text-red-400">
                            <span>Remise</span><span className="tabular-nums">−{money(totals.discount)} DH</span>
                        </div>
                    </>
                )}
                {tva.length > 0 && (
                    <div className="my-1.5 px-2.5 py-1.5 rounded-[10px] bg-gray-100 dark:bg-gray-700/60 text-[11px] text-gray-500 dark:text-gray-400">
                        {tva.map(({rate, amount}) => (
                            <div key={rate} className="flex justify-between">
                                <span>dont TVA {rate} %</span><span className="font-semibold tabular-nums">{money(amount)} DH</span>
                            </div>
                        ))}
                        {tva.length > 1 && (
                            <div className="flex justify-between font-bold">
                                <span>Total TVA incluse</span><span className="tabular-nums">{money(totals.tax)} DH</span>
                            </div>
                        )}
                    </div>
                )}
                {sale.note && (
                    <p className="flex items-center gap-1 text-xs italic text-gray-500 my-1">
                        <StickyNote className="w-3 h-3 flex-shrink-0"/><span className="truncate flex-1">{sale.note}</span>
                        <button type="button" onClick={onClearNote} aria-label="Retirer la note"><X className="w-3 h-3"/></button>
                    </p>
                )}
                <div className="flex justify-between items-baseline mt-1">
                    <span className="text-[17px] font-semibold tracking-[-0.01em] text-gray-900 dark:text-white">Total</span>
                    <span className="text-2xl font-bold tracking-[-0.02em] tabular-nums text-gray-900 dark:text-white">
                        {money(totals.total)} <span className="text-[13px] font-medium text-gray-500">DH</span>
                    </span>
                </div>
            </div>

            {/* Payer */}
            <button type="button" onClick={onPay} disabled={isEmpty || !employeeId}
                    className="mx-3 mb-3 flex-shrink-0 flex items-center justify-center gap-2.5 py-[15px] rounded-[14px] bg-primary text-white text-[17px] font-semibold tracking-[-0.01em] tabular-nums shadow-[0_6px_16px_rgba(77,182,172,0.4)] hover:bg-primary-dark active:scale-[0.99] transition disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:shadow-none disabled:cursor-not-allowed">
                {!isEmpty && <CreditCard className="w-5 h-5" strokeWidth={2.2}/>}
                {isEmpty ? "Payer" : `Payer ${formatMoney(totals.total)}`}
                {!isEmpty && <kbd className="hidden xl:inline ml-1 px-1.5 rounded bg-white/20 text-[11px] font-medium">F9</kbd>}
            </button>
        </div>
    );
};

export default PosCart;
